import { AxiosInstance, AxiosResponse } from 'axios';
import {
  AddPaymentRequest,
  AddSaleItemRequest,
  BatchStockInfo,
  Company,
  CompleteSaleRequest,
  CompleteSaleWithPaymentRequest,
  CreateAndCompleteSaleRequest,
  Customer,
  Item,
  PaginatedResponse,
  Sale,
  SaleItem,
  SalePayment,
  SalesSearchParams,
  SalesStatistics,
  TopSellingItem,
  StockValidationResult,
  UpdateSaleItemQuantityRequest,
} from '../../types';

export interface SalesReport {
  totalSales: number;
  totalRevenue: number;
  averageOrderValue: number;
  topCustomers: { customerId: string; customerName: string; totalPurchases: number }[];
  topItems: { itemId: string; itemName: string; totalQuantity: number }[];
  dailySales: { date: string; sales: number; revenue: number }[];
}

type SaleItemWithStock = Item & { availableStock: number; batches?: BatchStockInfo[] };

/** Sales workflow, reporting and sale-specific stock API calls. */
export class SalesApi {
  constructor(private readonly http: AxiosInstance) {}

  async getSales(params: SalesSearchParams = {}, signal?: AbortSignal): Promise<PaginatedResponse<Sale>> {
    const { sort, direction, ...filters } = params;
    const requestParams = {
        ...filters,
        ...(sort ? { sortBy: sort } : {}),
        ...(direction ? { sortDir: direction } : {}),
    };
    const requestConfig = signal ? { params: requestParams, signal } : { params: requestParams };
    const response: AxiosResponse<PaginatedResponse<Sale>> = await this.http.get('/sales', requestConfig);
    return response.data;
  }

  async getSalePayments(
    params: Pick<SalesSearchParams, 'page' | 'size' | 'startDate' | 'endDate' | 'companyId'>,
    signal?: AbortSignal,
  ): Promise<PaginatedResponse<SalePayment>> {
    const requestConfig = signal ? { params, signal } : { params };
    const response: AxiosResponse<PaginatedResponse<SalePayment>> = await this.http.get('/sales/payments', requestConfig);
    return response.data;
  }

  async getTopSellingItemsForRange(
    params: Pick<SalesSearchParams, 'startDate' | 'endDate' | 'companyId'> & { limit?: number },
    signal?: AbortSignal,
  ): Promise<TopSellingItem[]> {
    const requestConfig = signal ? { params, signal } : { params };
    const response: AxiosResponse<TopSellingItem[]> = await this.http.get('/sales/top-items', requestConfig);
    return response.data;
  }

  async getSaleById(saleId: string): Promise<Sale> {
    const response: AxiosResponse<Sale> = await this.http.get(`/sales/${saleId}`);
    return response.data;
  }

  async addItemToSale(saleId: string, request: AddSaleItemRequest): Promise<SaleItem> {
    const response: AxiosResponse<SaleItem> = await this.http.post(`/sales/${saleId}/items`, request);
    return response.data;
  }

  async updateSaleItemQuantity(saleItemId: string, request: UpdateSaleItemQuantityRequest): Promise<SaleItem> {
    const response: AxiosResponse<SaleItem> = await this.http.put(`/sales/items/${saleItemId}/quantity`, request);
    return response.data;
  }

  async removeItemFromSale(saleItemId: string): Promise<void> {
    await this.http.delete(`/sales/items/${saleItemId}`);
  }

  async completeSale(saleId: string, request: CompleteSaleRequest): Promise<Sale> {
    const response: AxiosResponse<Sale> = await this.http.post(`/sales/${saleId}/complete`, request);
    return response.data;
  }

  async createAndCompleteSale(request: CreateAndCompleteSaleRequest): Promise<Sale> {
    const response: AxiosResponse<Sale> = await this.http.post('/sales/create-and-complete', request);
    return response.data;
  }

  async completeSaleWithPayment(saleId: string, request: CompleteSaleWithPaymentRequest): Promise<Sale> {
    const response: AxiosResponse<Sale> = await this.http.post(`/sales/${saleId}/complete-with-payment`, request);
    return response.data;
  }

  async addPaymentToSale(saleId: string, request: AddPaymentRequest): Promise<SalePayment> {
    const response: AxiosResponse<SalePayment> = await this.http.post(`/sales/${saleId}/payments`, request);
    return response.data;
  }

  async cancelSale(saleId: string, reason?: string): Promise<Sale> {
    const params = reason ? { reason } : {};
    const response: AxiosResponse<Sale> = await this.http.post(`/sales/${saleId}/cancel`, null, { params });
    return response.data;
  }

  async getSalesStatistics(companyId?: string): Promise<SalesStatistics> {
    const response: AxiosResponse<SalesStatistics> = companyId
      ? await this.http.get('/sales/statistics', { params: { companyId } })
      : await this.http.get('/sales/statistics');
    return response.data;
  }

  async getSalesByCustomer(customerId: string, params: SalesSearchParams = {}): Promise<PaginatedResponse<Sale>> {
    const response: AxiosResponse<PaginatedResponse<Sale>> = await this.http.get('/sales', {
      params: { ...params, customerId },
    });
    return response.data;
  }

  async validateItemStock(itemId: string, quantity: number, batchId?: string): Promise<StockValidationResult> {
    const params = { quantity, ...(batchId && { batchId }) };
    const response: AxiosResponse<StockValidationResult> = await this.http.get(`/items/${itemId}/validate-stock`, { params });
    return response.data;
  }

  async getItemForSale(itemId: string): Promise<SaleItemWithStock> {
    const response: AxiosResponse<SaleItemWithStock> = await this.http.get(`/items/${itemId}/for-sale`);
    return response.data;
  }

  async searchItemsForSale(searchTerm: string): Promise<SaleItemWithStock[]> {
    const response: AxiosResponse<SaleItemWithStock[]> = await this.http.get('/items/search-for-sale', {
      params: { search: searchTerm },
    });
    return response.data;
  }

  async getItemsByCategoryForSale(categoryId: string): Promise<SaleItemWithStock[]> {
    const response: AxiosResponse<SaleItemWithStock[]> = await this.http.get(`/categories/${categoryId}/items-for-sale`);
    return response.data;
  }

  async searchItemsForSales(searchTerm: string): Promise<Item[]> {
    const response: AxiosResponse<Item[]> = await this.http.get('/items/search', { params: { name: searchTerm } });
    return response.data;
  }

  async getAvailableBatches(itemId: string): Promise<BatchStockInfo[]> {
    const response: AxiosResponse<BatchStockInfo[]> = await this.http.get(`/sales/items/${itemId}/available-batches`);
    return response.data;
  }

  async getSaleReceipt(saleId: string): Promise<Sale & { companyInfo: Company; customerInfo: Customer }> {
    const response: AxiosResponse<Sale & { companyInfo: Company; customerInfo: Customer }> =
      await this.http.get(`/sales/${saleId}/receipt`);
    return response.data;
  }

  async getTopSellingItems(limit = 10): Promise<{ itemId: string; itemName: string; totalQuantity: number; totalRevenue: number }[]> {
    const response: AxiosResponse<{ itemId: string; itemName: string; totalQuantity: number; totalRevenue: number }[]> =
      await this.http.get('/sales/top-selling-items', { params: { limit } });
    return response.data;
  }

  async getSalesReport(startDate: string, endDate: string): Promise<SalesReport> {
    const response: AxiosResponse<SalesReport> = await this.http.get('/sales/report', {
      params: { startDate, endDate },
    });
    return response.data;
  }
}
